import { useState } from "react";
import type { Order } from "@/mocks/orders";

interface OrderDetailModalProps {
  order: Order;
  onClose: () => void;
}

const statusSteps = ["processing", "shipped", "delivered"] as const;

export default function OrderDetailModal({ order, onClose }: OrderDetailModalProps) {
  const [currentStatus, setCurrentStatus] = useState(order.status);
  const [showStatusSelect, setShowStatusSelect] = useState(false);

  const statusColor = (status: string) => {
    switch (status) {
      case "delivered": return "bg-accent-100 text-accent-800 border-accent-300";
      case "shipped": return "bg-secondary-100 text-secondary-800 border-secondary-300";
      case "processing": return "bg-primary-100 text-primary-800 border-primary-300";
      case "cancelled": return "bg-foreground-100 text-foreground-600 border-foreground-300";
      default: return "bg-background-200 text-foreground-500";
    }
  };

  const handleStatusUpdate = (newStatus: string) => {
    setCurrentStatus(newStatus as Order["status"]);
    setShowStatusSelect(false);
  };

  const currentStepIndex = statusSteps.indexOf(currentStatus as typeof statusSteps[number]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-16 overflow-y-auto">
      <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-2xl border border-background-200/70">
        {/* Header */}
        <div className="px-6 py-4 border-b border-background-200/70 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-heading font-bold text-foreground-950">{order.orderNumber}</h3>
            <p className="text-sm text-foreground-500">{order.date}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Status + update */}
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Order Status</p>
              <span className={`inline-flex text-sm font-semibold px-3 py-1.5 rounded-full capitalize border ${statusColor(currentStatus)}`}>
                {currentStatus}
              </span>
            </div>
            {currentStatus !== "cancelled" && currentStatus !== "delivered" && (
              <div className="relative">
                <button
                  onClick={() => setShowStatusSelect(!showStatusSelect)}
                  className="px-4 py-2 text-sm font-medium bg-background-100 border border-background-200 rounded-lg text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap"
                >
                  <i className="ri-edit-line text-sm"></i>
                  Update Status
                </button>
                {showStatusSelect && (
                  <div className="absolute right-0 mt-2 bg-background-50 border border-background-200 rounded-xl p-1.5 z-10 min-w-[160px]">
                    {statusSteps.filter(s => s !== currentStatus).map((s) => (
                      <button
                        key={s}
                        onClick={() => handleStatusUpdate(s)}
                        className="w-full text-left px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer capitalize whitespace-nowrap"
                      >
                        {s}
                      </button>
                    ))}
                    <button
                      onClick={() => handleStatusUpdate("cancelled")}
                      className="w-full text-left px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Cancel Order
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Progress tracker */}
          {currentStatus !== "cancelled" && (
            <div className="flex items-center gap-0">
              {statusSteps.map((step, i) => (
                <div key={step} className="flex-1 flex items-center">
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                      i <= currentStepIndex
                        ? "bg-primary-500 text-white"
                        : "bg-background-200 text-foreground-400"
                    }`}>
                      {i < currentStepIndex ? (
                        <i className="ri-check-line"></i>
                      ) : (
                        i + 1
                      )}
                    </div>
                    <span className={`text-[11px] mt-1.5 capitalize whitespace-nowrap ${
                      i <= currentStepIndex ? "text-foreground-700 font-medium" : "text-foreground-400"
                    }`}>
                      {step}
                    </span>
                  </div>
                  {i < statusSteps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 mt-[-18px] ${
                      i < currentStepIndex ? "bg-primary-500" : "bg-background-200"
                    }`}></div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Items */}
          <div>
            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Items ({order.items.length})</p>
            <div className="space-y-3">
              {order.items.map((item, i) => (
                <div key={i} className="flex items-center gap-4 p-3 bg-background-100 rounded-xl">
                  <div className="w-12 h-12 rounded-lg bg-background-200 overflow-hidden flex-shrink-0">
                    <img src={item.imageUrl} alt={item.nameEn} className="w-full h-full object-cover object-top" loading="lazy" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground-900 truncate">{item.nameEn}</p>
                    <p className="text-xs text-foreground-500">
                      Qty: {item.quantity}
                      {Object.keys(item.selectedVariants).length > 0 && ` · ${Object.values(item.selectedVariants).join(", ")}`}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Shipping */}
            <div className="bg-background-100 rounded-xl p-4">
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Shipping Address</p>
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground-900">{order.shippingAddress.fullName}</p>
                <p className="text-sm text-foreground-600">{order.shippingAddress.street}</p>
                <p className="text-sm text-foreground-600">{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zip}</p>
                <p className="text-sm text-foreground-600">{order.shippingAddress.country}</p>
                <p className="text-sm text-foreground-600">{order.shippingAddress.phone}</p>
              </div>
              {order.trackingNumber && (
                <div className="mt-3 pt-3 border-t border-background-200">
                  <p className="text-xs text-foreground-500 mb-1">Tracking</p>
                  <p className="text-sm font-mono font-medium text-foreground-900">{order.trackingNumber}</p>
                </div>
              )}
            </div>

            {/* Payment + Totals */}
            <div className="bg-background-100 rounded-xl p-4">
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Payment</p>
              <p className="text-sm text-foreground-700 mb-3">{order.paymentMethod.type} ending in {order.paymentMethod.last4}</p>
              <div className="space-y-2 pt-3 border-t border-background-200">
                <div className="flex justify-between text-sm">
                  <span className="text-foreground-600">Subtotal</span>
                  <span className="text-foreground-900">${order.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-foreground-600">Shipping</span>
                  <span className="text-foreground-900">{order.shipping === 0 ? "Free" : `$${order.shipping.toFixed(2)}`}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-foreground-600">Tax</span>
                  <span className="text-foreground-900">${order.tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-base font-bold pt-2 border-t border-background-200">
                  <span className="text-foreground-900">Total</span>
                  <span className="text-foreground-950">${order.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}